// Typed client for the core API. Both dashboards call their own Worker at
// /api/v1/*; the Worker adds credentials (customer session cookie → bearer, or
// the Cloudflare Access JWT) and forwards to the engine. The browser never
// holds an API token.
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';

export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Fired when the API says the session is gone; the app sends the user to sign in. */
export const UNAUTHORISED_EVENT = 'cover:unauthorised';

const middleware: Middleware = {
  onRequest({ request }) {
    // The contract asks for an Idempotency-Key on every write, so a retried
    // request never duplicates a fact, asset or claim note.
    if (request.method !== 'GET' && !request.headers.has('Idempotency-Key')) request.headers.set('Idempotency-Key', crypto.randomUUID());
    return request;
  },
  async onResponse({ response }) {
    if (response.ok || response.status === 302) return response;
    let code = 'http_error';
    let message = `Request failed (${response.status})`;
    let details: Record<string, unknown> | undefined;
    try {
      const body = (await response.clone().json()) as { error?: { code: string; message: string; details?: Record<string, unknown> } };
      if (body.error) ({ code, message, details } = body.error);
    } catch {
      // not JSON
    }
    if (response.status === 401) window.dispatchEvent(new CustomEvent(UNAUTHORISED_EVENT));
    throw new ApiError(response.status, code, message, details);
  },
};

export function createApi(headers?: () => Record<string, string>) {
  const client = createClient<paths>({ baseUrl: API_BASE, credentials: 'same-origin' });
  client.use(middleware);
  if (headers)
    client.use({
      onRequest({ request }) {
        for (const [k, v] of Object.entries(headers())) request.headers.set(k, v);
        return request;
      },
    });
  return client;
}

export type Api = ReturnType<typeof createApi>;

/** Unwrap an openapi-fetch result (errors already throw in the middleware). */
export async function ok<T>(p: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const { data } = await p;
  return data as T;
}

/**
 * For endpoints not in the contract yet (proposed in docs/api-needs.md).
 * Same error handling as the typed client.
 */
export async function apiFetch<T>(path: string, init: RequestInit & { json?: unknown } = {}, headers: Record<string, string> = {}): Promise<T> {
  const h = new Headers({ ...headers, ...(init.headers as Record<string, string>) });
  if (init.json !== undefined) h.set('Content-Type', 'application/json');
  if (init.method && init.method !== 'GET' && !h.has('Idempotency-Key')) h.set('Idempotency-Key', crypto.randomUUID());
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: h, body: init.json !== undefined ? JSON.stringify(init.json) : init.body, credentials: 'same-origin' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { code: string; message: string; details?: Record<string, unknown> } };
    if (res.status === 401) window.dispatchEvent(new CustomEvent(UNAUTHORISED_EVENT));
    throw new ApiError(res.status, body.error?.code ?? 'http_error', body.error?.message ?? `Request failed (${res.status})`, body.error?.details);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** Multipart upload to POST /businesses/{id}/documents (openapi-fetch's multipart typing is awkward). */
export function uploadDocument(
  businessId: string,
  file: File,
  fields: { source: string; hint?: string; evidenceRef?: string; links?: import('./types.ts').Link[]; note?: string },
  headers: Record<string, string> = {},
) {
  const form = new FormData();
  form.set('file', file);
  form.set('source', fields.source);
  if (fields.hint) form.set('hint', fields.hint);
  if (fields.evidenceRef) form.set('evidenceRef', fields.evidenceRef);
  if (fields.links?.length) form.set('links', JSON.stringify(fields.links));
  if (fields.note) form.set('note', fields.note);
  return apiFetch<import('./types.ts').Document>(`/businesses/${encodeURIComponent(businessId)}/documents`, { method: 'POST', body: form }, headers);
}

/** A same-origin URL the browser can open; the Worker forwards the 302 to the signed file URL. */
export const documentUrl = (documentId: string) => `${API_BASE}/documents/${encodeURIComponent(documentId)}/content`;
export const certificateUrl = (policyNumber: string) => `${API_BASE}/policies/${encodeURIComponent(policyNumber)}/certificate`;
